import { useState, useEffect, useCallback, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { LoginCard } from '../components/LoginCard';
import { Toast } from '../components/Toast';
import { Eye, EyeOff, Loader2 } from 'lucide-react';

function validateEmail(email: string): string | null {
  if (!email) return null;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? null : 'Enter a valid email address.';
}

function validatePassword(password: string): string | null {
  if (!password) return null;
  if (password.length < 8) return 'At least 8 characters.';
  if (!/[A-Z]/.test(password)) return 'Needs an uppercase letter.';
  if (!/[a-z]/.test(password)) return 'Needs a lowercase letter.';
  if (!/[0-9]/.test(password)) return 'Needs a number.';
  return null;
}

export default function LoginPage() {
  const { login, completeNewPassword, isAuthenticated, needsNewPassword } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'error' | 'success' } | null>(null);
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/fleet', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const dismissToast = useCallback(() => setToast(null), []);

  function triggerShake() {
    setIsShaking(true);
    setTimeout(() => setIsShaking(false), 500);
  }

  function showError(message: string) {
    setToast({ message, type: 'error' });
    triggerShake();
  }

  const emailError = touched.email ? validateEmail(email) : null;
  const passwordError = touched.password ? validatePassword(password) : null;
  const newPasswordError = touched.newPassword ? validatePassword(newPassword) : null;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setTouched({ email: true, password: true });

    const eErr = validateEmail(email);
    const pErr = validatePassword(password);
    if (eErr || pErr) {
      triggerShake();
      return;
    }

    setIsSubmitting(true);
    try {
      await login(email, password);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('not authorized')) {
        showError('Invalid email or password. Please check your credentials and try again.');
      } else if (msg.includes('User not found')) {
        showError('No account found with this email. Contact your administrator for access.');
      } else if (msg.includes('Incorrect password')) {
        showError('Incorrect password. Please try again.');
      } else if (msg.includes('User is disabled')) {
        showError('This account has been disabled. Contact your administrator.');
      } else if (msg.includes('too many failed')) {
        showError('Too many failed attempts. Please wait a few minutes before trying again.');
      } else {
        showError('Sign-in failed. Please check your connection and try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleNewPassword(e: FormEvent) {
    e.preventDefault();
    setTouched({ newPassword: true, confirmPassword: true });

    if (newPassword !== confirmPassword) {
      showError('Passwords do not match. Please make sure both fields are identical.');
      return;
    }
    const npErr = validatePassword(newPassword);
    if (npErr) {
      triggerShake();
      return;
    }

    setIsSubmitting(true);
    try {
      await completeNewPassword(newPassword);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('invalid password')) {
        showError('Password does not meet requirements. Please use a stronger password.');
      } else if (msg.includes('password has been used')) {
        showError('You have used this password before. Please choose a different one.');
      } else {
        showError('Unable to set new password. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-0 px-4 relative overflow-hidden">
      {/* Ambient background */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 120%, rgba(0,111,199,0.15) 0%, transparent 70%), ' +
            'radial-gradient(ellipse 60% 50% at 20% 80%, rgba(12,141,233,0.08) 0%, transparent 60%)',
        }}
      />

      <div className="w-full max-w-sm relative z-10">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-kinetica-600 rounded-2xl text-white font-bold text-2xl mb-4">
            K
          </div>
          <h1 className="text-xl font-semibold text-white">Kinetica Portal</h1>
          <p className="text-sm text-gray-500 mt-1">Sign in to manage your fleet</p>
        </div>

        {/* Toast */}
        {toast && (
          <div className="mb-4">
            <Toast message={toast.message} type={toast.type} onDismiss={dismissToast} />
          </div>
        )}

        {/* Form */}
        <LoginCard isShaking={isShaking}>
          {needsNewPassword ? (
            <form onSubmit={handleNewPassword} className="card space-y-4" aria-label="Set new password">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg px-4 py-3 text-sm text-blue-400">
                <strong>Security notice:</strong> For your protection, you're required to set a new password before continuing.
              </div>

              <div>
                <label htmlFor="new-password" className="block text-xs font-medium text-gray-400 mb-1.5">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, newPassword: true }))}
                    className="input w-full pr-10"
                    placeholder="At least 8 characters"
                    required
                    autoFocus
                    autoComplete="new-password"
                    aria-describedby={newPasswordError ? 'new-password-error' : undefined}
                    aria-invalid={newPasswordError ? true : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-300 transition-colors"
                    aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                  >
                    {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {newPasswordError && (
                  <p id="new-password-error" className="text-xs text-red-400 mt-1">{newPasswordError}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirm-password" className="block text-xs font-medium text-gray-400 mb-1.5">
                  Confirm Password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, confirmPassword: true }))}
                  className="input w-full"
                  placeholder="Re-enter password"
                  required
                  autoComplete="new-password"
                  aria-label="Confirm new password"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full h-10 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : 'Set Password & Sign In'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="card space-y-4" aria-label="Sign in">
              <div>
                <label htmlFor="email" className="block text-xs font-medium text-gray-400 mb-1.5">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                  className="input w-full"
                  placeholder="you@company.com"
                  required
                  autoFocus
                  autoComplete="email"
                  aria-describedby={emailError ? 'email-error' : undefined}
                  aria-invalid={emailError ? true : undefined}
                />
                {emailError && (
                  <p id="email-error" className="text-xs text-red-400 mt-1">{emailError}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-xs font-medium text-gray-400 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, password: true }))}
                    className="input w-full pr-10"
                    placeholder="Enter password"
                    required
                    autoComplete="current-password"
                    aria-describedby={passwordError ? 'password-error' : undefined}
                    aria-invalid={passwordError ? true : undefined}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-300 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
                {passwordError && (
                  <p id="password-error" className="text-xs text-red-400 mt-1">{passwordError}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full h-10 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? <Loader2 size={18} className="animate-spin" /> : 'Sign In'}
              </button>
            </form>
          )}
        </LoginCard>

        <div className="text-center mt-6">
          <p className="text-[11px] text-gray-600">
            Need help? Contact your organization administrator for access support.
          </p>
        </div>
      </div>
    </div>
  );
}
